/** Layout visibility for global header, footer, chat, and admin chrome. */
export interface AppShellRouteState {
  isAdminPanel: boolean;
  isAdminDashboardShell: boolean;
  hideChatbot: boolean;
  showHeader: boolean;
  showFooter: boolean;
}

const SELLER_ROUTE_PREFIXES = [
  '/seller-dashboard',
  '/seller-profile',
  '/seller-list',
  '/seller-update-password',
  '/seller-reset-password',
  '/seller-child-login',
  '/seller-complaints',
  '/seller-assistant',
  '/add-member-seller',
  '/change-password-seller',
  '/reset-seller-password',
  '/seller-auth',
  '/seller-login',
  '/seller-signup',
  '/sellar-login',
  '/sellar-signup',
  '/sellar-member-login',
] as const;

/** Customer/seller/delivery sign-in and sign-up — no shop header, footer, or widgets. */
const PUBLIC_AUTH_PATHS = [
  '/login',
  '/signup',
  '/update-password',
  '/sellar-login',
  '/sellar-signup',
  '/sellar-member-login',
  '/delivery/login',
  '/delivery/signup',
  '/admin-login',
  '/oauth-callback',
  '/customer/login',
  '/forgot-password',
] as const;

export function isDeliveryRoute(url: string): boolean {
  const path = (url || '').split('?')[0].split('#')[0].toLowerCase();
  return path === '/delivery' || path.startsWith('/delivery/');
}

export function isSellerRoute(url: string): boolean {
  return SELLER_ROUTE_PREFIXES.some((prefix) => url.includes(prefix));
}

export function isPublicAuthRoute(url: string): boolean {
  const path = (url || '').split('?')[0].split('#')[0].toLowerCase();
  if (!path || path === '/') {
    return false;
  }
  return PUBLIC_AUTH_PATHS.some((segment) => path === segment || path.startsWith(`${segment}/`));
}

/**
 * Derive shell UI flags from the active router URL.
 * Used on NavigationEnd and on initial bootstrap.
 */
function isBrandSiteRoute(url: string): boolean {
  const path = (url || '').split('?')[0].split('#')[0];
  return /^\/brand\/[^/]+/i.test(path);
}

function isAiSellerSiteRoute(url: string): boolean {
  const path = (url || '').split('?')[0].split('#')[0];
  return /^\/site\/[^/]+/i.test(path);
}

export function resolveAppShellRouteState(url: string): AppShellRouteState {
  const isAdminLoginPage = url.includes('/admin-login');
  const isAdminPanel = url.startsWith('/Admin-Panel') && !isAdminLoginPage;
  const isAdminDashboardShell = url.includes('/Admin-Panel/Dashboard');
  const sellerRoute = isSellerRoute(url);
  const deliveryRoute = isDeliveryRoute(url);
  const brandSiteRoute = isBrandSiteRoute(url);
  const aiSellerSiteRoute = isAiSellerSiteRoute(url);
  const storefrontImmersive = brandSiteRoute || aiSellerSiteRoute;
  const authRoute = isPublicAuthRoute(url);

  const hideForAdminOrAuth =
    ((url.includes('admin') || url.includes('Admin-Panel')) && !isAdminLoginPage) ||
    (url.includes('login') && !isAdminLoginPage && !deliveryRoute) ||
    (url.includes('signup') && !deliveryRoute) ||
    sellerRoute ||
    deliveryRoute ||
    authRoute;

  const hideHeaderFooterForPanel =
    url.startsWith('/Admin-Panel') ||
    url.startsWith('/Seller-Panel') ||
    url.startsWith('/seller-dashboard') ||
    deliveryRoute;

  const showPublicChrome = !hideHeaderFooterForPanel || isAdminLoginPage;
  const showShopChrome = showPublicChrome && !authRoute;

  return {
    isAdminPanel,
    isAdminDashboardShell,
    hideChatbot: hideForAdminOrAuth || isImmersiveStreamViewerRoute(url) || storefrontImmersive,
    showHeader:
      showShopChrome &&
      !storefrontImmersive &&
      !deliveryRoute &&
      ((!url.includes('/admin') && !url.includes('/Admin-Panel')) || isAdminLoginPage) &&
      !url.includes('/seller-dashboard') &&
      !isImmersiveStreamViewerRoute(url),
    showFooter:
      showShopChrome &&
      !storefrontImmersive &&
      ((!url.includes('/admin') && !url.includes('/Admin-Panel')) || isAdminLoginPage) &&
      !sellerRoute &&
      !deliveryRoute &&
      !isImmersiveStreamViewerRoute(url),
  };
}

function isImmersiveStreamViewerRoute(url: string): boolean {
  const path = (url || '').split('?')[0].split('#')[0];
  return /^\/live-streaming(\/|$)/i.test(path) || /^\/streams\/[^/]+/i.test(path);
}
